import { useState } from 'react';
import { regionTags } from '../../data/contact';
import { SITE } from '../../lib/site';
import { Icon } from '../Icon';
import { Picture } from '../Picture';

/**
 * Studio location card. Shows a static map image; the Google Maps iframe is only created
 * after the visitor clicks "Load interactive map", so nothing is requested from Google before.
 */
export function StudioMap() {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="bg-surface-ivory rounded p-space-md shadow-sm flex flex-col gap-space-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-space-xs">
          <Icon name="map" size={20} className="text-secondary" />
          <h3 className="font-headline-sm text-[18px] leading-7 text-primary">Kanyakumari &amp; Travancore Hub</h3>
        </div>
        <span className="font-label-sm text-label-sm uppercase px-space-xs py-0.5 rounded bg-surface-cream text-on-surface-variant">
          Geographic Node
        </span>
      </div>

      <div className="relative">
        <div className="relative w-full h-72 rounded overflow-hidden shadow-inner group bg-surface-container">
          {loaded ? (
            <iframe
              src={SITE.mapsEmbedUrl}
              title="Map of Green Valley Interiors studio in Nagercoil"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          ) : (
            <>
              <Picture
                name="contact-map-nagercoil"
                alt="Street map of Nagercoil marking the Green Valley Interiors studio at Praise Complex, Asaripallam Road"
                sizes="(min-width: 1024px) 38vw, 100vw"
                className="block w-full h-full"
                imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-x-0 top-0 bottom-20 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => setLoaded(true)}
                  aria-label="Load interactive map of the Nagercoil studio (Google Maps)"
                  className="inline-flex items-center gap-space-xs bg-primary/95 text-gold-light hover:bg-primary-container px-space-md py-space-sm rounded font-label-md text-label-md uppercase tracking-wider shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-dark transition-colors"
                >
                  <Icon name="map" size={18} />
                  <span>Load interactive map</span>
                </button>
              </div>
            </>
          )}
        </div>

        <div
          className={`bg-surface-ivory/90 backdrop-blur-md p-space-sm rounded shadow-md flex items-center justify-between gap-space-sm ${
            loaded ? 'mt-space-sm' : 'absolute bottom-3 left-3 right-3'
          }`}
        >
          <div className="flex flex-col min-w-0">
            <span className="font-label-sm text-label-sm uppercase tracking-wider text-primary font-bold">Green Valley Design HQ</span>
            <span className="font-body-sm text-body-sm text-on-surface-variant truncate">
              {SITE.address.locality} • 65km south of Trivandrum Airport
            </span>
          </div>
          <a
            href={SITE.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open in Google Maps (opens in a new tab)"
            className="p-2 rounded bg-primary text-gold-light hover:bg-primary-container transition-colors shrink-0"
          >
            <Icon name="near_me" size={18} />
          </a>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-space-sm gap-y-1">
        <p className="font-body-sm text-[12px] leading-5 text-on-surface-variant">
          The interactive map loads from Google only when you choose to.
        </p>
        <a
          href={SITE.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-label-sm text-label-sm uppercase tracking-wider text-primary hover:text-secondary underline underline-offset-4"
        >
          Open in Google Maps <Icon name="open_in_new" size={14} />
        </a>
      </div>

      <ul className="flex flex-wrap gap-space-xs pt-space-xs" aria-label="Areas we serve">
        {regionTags.map((r) => (
          <li
            key={r}
            className="font-label-sm text-label-sm uppercase tracking-wider px-space-sm py-1 rounded bg-surface-cream text-on-surface-variant"
          >
            {r}
          </li>
        ))}
      </ul>
    </div>
  );
}
